import React, { useState } from 'react';
import type { OrderSummaryData } from '../../types';
import type {
  DiscountBreakdown,
  GiftProgressState,
  InstallmentInfo,
  OrderBumpCard,
} from '../../offer.types';
import { OrderSummaryHeader } from './components/OrderSummaryHeader';
import { CouponSection } from './components/CouponSection';
import { ProductsList } from './components/ProductsList';
import { AcceptedBumpsList } from './components/AcceptedBumpsList';
import { OrderSummaryFooter } from './components/OrderSummaryFooter';
import GiftSection from '../GiftSection';
import { useOrderSummary } from './hooks/useOrderSummary';
import type { CheckoutSummaryConfig } from '../../services/checkoutConfig/checkoutConfig.types';

interface OrderSummaryCardProps {
  summary: OrderSummaryData;
  onApplyCoupon?: (couponCode: string) => Promise<void>;
  couponError?: string;
  isLoading?: boolean;
  discountBreakdown?: DiscountBreakdown;
  installmentInfo?: InstallmentInfo;
  orderBumps?: OrderBumpCard[];
  acceptedOrderBumpNanoIds?: string[];
  onOrderBumpsChange?: (nextAcceptedNanoIds: string[]) => void;
  giftStates?: GiftProgressState[];
  onApplyGiftCoupon?: (couponCode: string) => Promise<void>;
  /** `config.summary` do checkout — controla abertura default no mobile, parcelas e cupom. */
  config?: CheckoutSummaryConfig;
}

const OrderSummaryCard: React.FC<OrderSummaryCardProps> = ({
  summary,
  onApplyCoupon,
  couponError,
  isLoading = false,
  discountBreakdown,
  installmentInfo,
  orderBumps,
  acceptedOrderBumpNanoIds,
  onOrderBumpsChange,
  giftStates,
  onApplyGiftCoupon,
  config,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(!config?.defaultOpenOnMobile);
  const { finalTotal, totalProducts } = useOrderSummary(summary);
  const showInstallments = config?.showInstallments ?? true;
  const showCoupon = config?.showCoupon ?? true;
  const effectiveInstallmentInfo = showInstallments ? installmentInfo : undefined;

  return (
    <div className="bg-card-bg-light dark:bg-card-bg-dark shadow-md dark:shadow-lg rounded-lg overflow-hidden">
              <OrderSummaryHeader
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          totalProducts={totalProducts}
          finalTotal={finalTotal}
          hasCoupon={!!summary.couponCode}
          installmentInfo={effectiveInstallmentInfo}
        />

      <div className={`${isCollapsed ? 'hidden lg:block' : 'block'} p-4 space-y-4`}>
        {onApplyCoupon && showCoupon && (
          <CouponSection
            summary={summary}
            onApplyCoupon={onApplyCoupon}
            couponError={couponError}
            isLoading={isLoading}
          />
        )}

        {giftStates && giftStates.length > 0 && (
          <GiftSection
            states={giftStates}
            onApplyCoupon={onApplyGiftCoupon}
            loading={isLoading}
            compact
          />
        )}

        <ProductsList products={summary.products ?? []} />

        {orderBumps && orderBumps.length > 0 && (acceptedOrderBumpNanoIds?.length ?? 0) > 0 && (
          <AcceptedBumpsList
            bumps={orderBumps}
            acceptedNanoIds={acceptedOrderBumpNanoIds ?? []}
            onRemove={
              onOrderBumpsChange
                ? (nanoId) =>
                    onOrderBumpsChange((acceptedOrderBumpNanoIds ?? []).filter((id) => id !== nanoId))
                : undefined
            }
          />
        )}

        <OrderSummaryFooter
          summary={summary}
          finalTotal={finalTotal}
          discountBreakdown={discountBreakdown}
          installmentInfo={effectiveInstallmentInfo}
        />
      </div>
    </div>
  );
};

export default OrderSummaryCard; 